import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { CSVLink } from 'react-csv';
import { FaFileExport } from 'react-icons/fa';
import classNames from 'classnames';

import { getAllDealsByAdmin } from './queries';
import FilterFromToV2 from '../../../components/Filter/FilterFromToV2';
import styles from './styles';
import { colors } from '../../../constants';

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: colors.parimary,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class TableDeal extends Component {
  state = {
    offer: '',
    merchant: '',
    consumer: '',
    minOfferValue: '',
    maxOfferValue: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 15,
    exportData: [],
    exporting: false,
  };

  componentDidUpdate(prevProps, prevState) {
    const filterKeys = [
      'page', 'limit', 'offer', 'merchant', 'consumer',
      'minOfferValue', 'maxOfferValue', 'dateFrom', 'dateTo',
    ];
    const changed = filterKeys.some((key) => prevState[key] !== this.state[key]);
    if (changed) {
      this.refetchData();
    }
  }

  buildFiltersInput = () => {
    const {
      offer, merchant, consumer, minOfferValue, maxOfferValue, dateFrom, dateTo,
    } = this.state;
    return {
      offer: offer || null,
      merchant: merchant || null,
      consumer: consumer || null,
      minOfferValue: minOfferValue !== '' ? parseFloat(minOfferValue) : null,
      maxOfferValue: maxOfferValue !== '' ? parseFloat(maxOfferValue) : null,
      fromDate: dateFrom || null,
      toDate: dateTo || null,
    };
  };

  refetchData = () => {
    const { refetch } = this.props;
    if (!refetch) return;
    const { page, limit } = this.state;
    refetch({
      input: {
        ...this.buildFiltersInput(),
        page: page || 1,
        limit: limit || 15,
      },
    });
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value, page: 1 });
  };

  handleChangePage = (event, page) => {
    this.setState({ page: page + 1 });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ limit: event.target.value, page: 1 });
  };

  getOfferTitle = (deal) => (deal.offer && deal.offer.title ? deal.offer.title : '');

  getMerchantName = (deal) => (deal.merchant && deal.merchant.username ? deal.merchant.username : '');

  getConsumerName = (deal) => (deal.consumer && deal.consumer.username ? deal.consumer.username : '');

  getDealDate = (deal) => {
    if (!deal.createdAt) return null;
    const timestamp = Number(deal.createdAt);
    const date = !isNaN(timestamp) && timestamp > 0
      ? moment(timestamp)
      : moment(deal.createdAt);
    return date.isValid() ? date : null;
  };

  formatRow = (deal) => {
    const date = this.getDealDate(deal);
    return {
      Offer: this.getOfferTitle(deal),
      Merchant: this.getMerchantName(deal),
      Consumer: this.getConsumerName(deal),
      'Offer-Value': deal.offerPrice != null ? deal.offerPrice : '',
      'DealCoins-CashBack': deal.coinFromEarnedDealCoin != null ? deal.coinFromEarnedDealCoin : '',
      'Date-Time': date ? date.format('MM/DD/YYYY HH:mm') : '',
    };
  };

  prepareDataForCSV = (deals = []) => deals.map((deal) => this.formatRow(deal));

  onExportToCSV = () => {
    const { client } = this.props;
    const { limit } = this.state;
    if (!client) return;

    this.setState({ exporting: true }, async () => {
      try {
        const baseInput = {
          ...this.buildFiltersInput(),
          page: 1,
          limit: limit || 15,
        };

        const firstResult = await client.query({
          query: getAllDealsByAdmin,
          variables: { input: baseInput },
          fetchPolicy: 'no-cache',
        });
        const firstPayload = firstResult && firstResult.data
          ? firstResult.data.getAllDealsByAdmin
          : null;

        if (!firstPayload || !firstPayload.count_rows) {
          this.setState({ exporting: false, exportData: [] });
          return;
        }

        let allDeals = (firstPayload.deals || []).slice();
        const totalPage = firstPayload.total_page || 1;

        for (let currentPage = 2; currentPage <= totalPage; currentPage += 1) {
          const pageResult = await client.query({
            query: getAllDealsByAdmin,
            variables: { input: { ...baseInput, page: currentPage } },
            fetchPolicy: 'no-cache',
          });
          const pagePayload = pageResult && pageResult.data
            ? pageResult.data.getAllDealsByAdmin
            : null;
          if (pagePayload && pagePayload.deals && pagePayload.deals.length) {
            allDeals = allDeals.concat(pagePayload.deals);
          }
        }

        this.setState({
          exportData: this.prepareDataForCSV(allDeals),
          exporting: false,
        }, () => {
          if (this.csvLinkRef) {
            this.csvLinkRef.link.click();
          }
        });
      } catch (error) {
        this.setState({ exporting: false });
      }
    });
  };

  renderFilter = () => {
    const {
      offer, merchant, consumer, minOfferValue, maxOfferValue, dateFrom, dateTo,
    } = this.state;
    const { classes } = this.props;

    return (
      <div>
        <div className={classes.filterRow}>
          <FilterFromToV2
            label="Offer"
            fromField={
              <TextField name="offer" value={offer} onChange={this.handleFilterChange} placeholder="Offer" />
            }
            resetFn={() => this.setState({ offer: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Merchant"
            fromField={
              <TextField name="merchant" value={merchant} onChange={this.handleFilterChange} placeholder="Merchant" />
            }
            resetFn={() => this.setState({ merchant: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Consumer"
            fromField={
              <TextField name="consumer" value={consumer} onChange={this.handleFilterChange} placeholder="Consumer" />
            }
            resetFn={() => this.setState({ consumer: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Min Offer Value"
            fromField={
              <TextField
                name="minOfferValue"
                type="number"
                value={minOfferValue}
                onChange={this.handleFilterChange}
                placeholder="Min"
              />
            }
            resetFn={() => this.setState({ minOfferValue: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Max Offer Value"
            fromField={
              <TextField
                name="maxOfferValue"
                type="number"
                value={maxOfferValue}
                onChange={this.handleFilterChange}
                placeholder="Max"
              />
            }
            resetFn={() => this.setState({ maxOfferValue: '', page: 1 })}
            smallButton
          />
        </div>
        <div className={classes.filterRow}>
          <FilterFromToV2
            label="From Date"
            fromField={
              <TextField
                name="dateFrom"
                type="date"
                value={dateFrom}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ dateFrom: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="To Date"
            fromField={
              <TextField
                name="dateTo"
                type="date"
                value={dateTo}
                onChange={this.handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            }
            resetFn={() => this.setState({ dateTo: '', page: 1 })}
            smallButton
          />
        </div>
      </div>
    );
  };

  renderToolBar = () => {
    const { classes, dealsData } = this.props;
    const { exportData, exporting } = this.state;
    const countRows = (dealsData && dealsData.count_rows) || 0;

    return (
      <div className={classes.searchWrapper}>
        <Button
          variant="contained"
          color="primary"
          className={classNames(classes.buttonActive, classes.exportButton)}
          onClick={this.onExportToCSV}
          disabled={exporting}
        >
          <Typography component="p" className={classes.text}>
            {exporting ? 'Exporting...' : 'Export Filtered to CSV'}
          </Typography>
          <FaFileExport color="#ffffff" size={16} />
        </Button>
        <CSVLink
          data={exportData}
          filename={`deals-${moment().format('YYYY-MM-DD-HHmmss')}.csv`}
          style={{ display: 'none' }}
          ref={(r) => { this.csvLinkRef = r; }}
        >
          Export
        </CSVLink>
        <Typography component="p" style={{ fontSize: '14px' }}>
          Rows: {countRows}
        </Typography>
      </div>
    );
  };

  render() {
    const { classes, dealsData, loading } = this.props;
    const { page, limit } = this.state;

    if (loading && !dealsData) return null;

    const deals = (dealsData && dealsData.deals) || [];
    const countRows = (dealsData && dealsData.count_rows) || 0;

    return (
      <div>
        {this.renderFilter()}
        {this.renderToolBar()}
        <Paper className={classes.root}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <CustomTableCell>Offer</CustomTableCell>
                <CustomTableCell>Merchant</CustomTableCell>
                <CustomTableCell>Consumer</CustomTableCell>
                <CustomTableCell align="right">Offer-Value</CustomTableCell>
                <CustomTableCell align="right">DealCoins-CashBack</CustomTableCell>
                <CustomTableCell align="right">Date-Time</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal) => {
                const row = this.formatRow(deal);
                return (
                  <TableRow className={classes.row} key={deal.id}>
                    <CustomTableCell component="th" scope="row">{row.Offer}</CustomTableCell>
                    <CustomTableCell>{row.Merchant}</CustomTableCell>
                    <CustomTableCell>{row.Consumer}</CustomTableCell>
                    <CustomTableCell align="right">{row['Offer-Value']}</CustomTableCell>
                    <CustomTableCell align="right">{row['DealCoins-CashBack']}</CustomTableCell>
                    <CustomTableCell align="right">{row['Date-Time']}</CustomTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={countRows}
            rowsPerPage={limit}
            page={page - 1}
            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    );
  }
}

TableDeal.defaultProps = {
  classes: {},
  dealsData: null,
  loading: false,
  refetch: () => {},
  client: null,
};

TableDeal.propTypes = {
  classes: PropTypes.object,
  dealsData: PropTypes.object,
  loading: PropTypes.bool,
  refetch: PropTypes.func,
  client: PropTypes.object,
};

const AllDealsByAdmin = graphql(getAllDealsByAdmin, {
  options: () => ({
    variables: {
      input: {
        page: 1,
        limit: 15,
        offer: null,
        merchant: null,
        consumer: null,
        minOfferValue: null,
        maxOfferValue: null,
        fromDate: null,
        toDate: null,
      },
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => ({
    loading: data.loading,
    dealsData: data.getAllDealsByAdmin,
    refetch: data.refetch,
  }),
});

export default withStyles(styles)(compose(AllDealsByAdmin, withApollo)(TableDeal));
